import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {SMSApi} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const smsApi = new SMSApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface SMSSendMessagePopupProps {
    onClose : OnClose
}

export const SMSSendMessagePopup = ({onClose} : SMSSendMessagePopupProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The message to send
     */
    const [message, setMessage] = useState<string>("");
    /**
     * The telephone number to send an SMS text message to
     */
    const [telephoneNumber, setTelephoneNumber] = useState<string>("");
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true if the message was sent
     */
    const [sent, setSent] = useState<boolean>(false);
    /**
     * Ref for telephone number input field
     */
    const telephoneInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const send = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSending(true);
        setSent(false)

        smsApi.sMSControllerSendSMSMessage(telephoneNumber, message).then(() => {
            setSent(true)
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error sending SMS", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => telephoneInputRef.current?.focus()}
               scrollable={true}
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("sms.send-sms-title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        {sent &&
                            <RenderAlert message={t('sms.sms-sent')} variant="success"/>
                        }

                        <FloatingLabel label={t("sms.phone-number")} className="text-secondary mb-3">
                            <Form.Control type="tel" value={telephoneNumber} placeholder={t("sms.phone-number")}
                                          ref={telephoneInputRef}
                                          onChange={e => setTelephoneNumber(e.target.value)}/>
                        </FloatingLabel>

                       <Form.Group className="mb-3">
                            <Form.Label className="text-secondary">{t("sms.message")}</Form.Label>
                            <Form.Control
                                className="font-monospace white-space-pre"
                                as="textarea"
                                onChange={e => setMessage(e.target.value)}
                                value={message}
                                rows={5}
                            />
                        </Form.Group>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={send}
                        disabled={StringUtils.isBlank(telephoneNumber) || StringUtils.isBlank(message)}>
                    {sending && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {sending ? t("common.sending") : t("common.send")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
